import { Controller } from "@hotwired/stimulus"
import { debounce } from "../util"

const ERRORS = {
  emailRequired: "Email is required",
  invalidEmail: "Invalid email format",
  emailConfirmationRequired: "Email confirmation is required",
  emailConfirmationMismatch: "Email confirmation does not match"
}

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const EMAIL_INPUT_DEBOUNCE_MS = 500

export default class extends Controller {
  static targets = [
    "emailInput",
    "emailConfirmationInput",
    "emailError",
    "emailConfirmationError",
    "serverError"
  ]

  connect() {
    this.attachEmailListeners()
    this.attachEmailConfirmationListeners()
  }

  attachEmailListeners() {
    if (!this.hasEmailInputTarget) return

    this.emailInputTarget.addEventListener("focus", this.handleEmailFocus.bind(this))
    this.emailInputTarget.addEventListener("blur", this.handleEmailBlur.bind(this))

    if (this.hasEmailConfirmationInputTarget) {
      this.emailInputTarget.addEventListener(
        "input",
        debounce(this.revalidateConfirmation.bind(this), EMAIL_INPUT_DEBOUNCE_MS)
      )
    }
  }

  attachEmailConfirmationListeners() {
    if (!this.hasEmailConfirmationInputTarget) return

    this.emailConfirmationInputTarget.addEventListener("focus", this.handleConfirmationFocus.bind(this))
    this.emailConfirmationInputTarget.addEventListener("blur", this.handleConfirmationBlur.bind(this))
  }

  handleEmailFocus() {
    this.hideError(this.emailErrorTarget)
    this.clearServerErrors()
  }

  handleConfirmationFocus() {
    this.hideError(this.emailConfirmationErrorTarget)
    this.clearServerErrors()
  }

  handleEmailBlur() {
    const value = this.emailInputTarget.value.trim()
    this.toggleError(this.emailErrorTarget, this.emailErrorMessage(value))
  }

  handleConfirmationBlur() {
    const value = this.emailConfirmationInputTarget.value.trim()
    this.toggleError(this.emailConfirmationErrorTarget, this.confirmationErrorMessage(value))
  }

  revalidateConfirmation() {
    const value = this.emailConfirmationInputTarget.value.trim()
    if (value === "") return

    this.toggleError(this.emailConfirmationErrorTarget, this.confirmationFormatOrMismatchMessage(value))
  }

  emailErrorMessage(value) {
    if (value === "") return ERRORS.emailRequired
    if (!this.isValidEmail(value)) return ERRORS.invalidEmail
    return null
  }

  confirmationErrorMessage(value) {
    if (value === "") return ERRORS.emailConfirmationRequired
    return this.confirmationFormatOrMismatchMessage(value)
  }

  confirmationFormatOrMismatchMessage(value) {
    if (!this.isValidEmail(value)) return ERRORS.invalidEmail
    if (value !== this.emailValue()) return ERRORS.emailConfirmationMismatch
    return null
  }

  emailValue() {
    return this.emailInputTarget.value.trim()
  }

  isValidEmail(value) {
    return EMAIL_PATTERN.test(value)
  }

  clearServerErrors() {
    if (!this.hasServerErrorTarget) return
    this.serverErrorTargets.forEach(serverError => serverError.remove())
  }

  hideError(errorElement) {
    this.toggleError(errorElement, null)
  }

  toggleError(errorElement, message) {
    if (!errorElement) return

    const hasError = message !== null
    errorElement.classList.toggle("hidden", !hasError)
    errorElement.classList.toggle("error", hasError)
    errorElement.textContent = hasError ? message : ""
    this.notifyValidationChange()
  }

  notifyValidationChange() {
    this.dispatch("change", { detail: { hasError: this.hasAnyError() } })
  }

  hasAnyError() {
    const emailHasError = this.hasEmailErrorTarget && this.emailErrorTarget.classList.contains("error")
    const confirmationHasError = this.hasEmailConfirmationErrorTarget && this.emailConfirmationErrorTarget.classList.contains("error")
    return emailHasError || confirmationHasError
  }
}
